import { Button, Checkbox, Icon, Select } from '@/components';
import { FadGroupPicker } from '@/features/release-config/FadGroupPicker';
import { useReleaseProducts } from '@/features/release-config/hooks';
import { cn } from '@/lib/cn';
import {
  ADAPTER_WORKFLOW_TEMPLATES,
  type GitWorkflowRow,
  RELEASE_ADAPTER_LABELS,
  type ReleaseEnvironment,
  type WorkflowFileMeta,
  type WorkflowInputOverrides,
  adapterWorkflowFor,
  workflowStem,
} from '@uchat-team-management/shared';
import { For, Show, createEffect, createMemo, createSignal } from 'solid-js';
import { useGitWorkflows } from './hooks';

// Injected by the system at dispatch; never user-overridable (mirrors SYSTEM_INPUT_KEYS).
const SYSTEM_INPUT_NAMES = new Set([
  'version',
  'buildNumber',
  'environment',
  'commitSha',
  'uploadToken',
  'apiUrl',
]);

/**
 * "Injected by the release" grid — the system inputs dispatch always sends.
 * uploadToken/apiUrl are also injected for desktop_app but deliberately never
 * shown here (UCH-463); reveal is API-only via GET /:releaseId/secrets.
 */
export const InjectedVars = (props: {
  version: string;
  buildNumber: number;
  environment: string;
  commitSha: string;
}) => {
  const vars = (): { name: string; value: string }[] => [
    { name: 'version', value: props.version },
    { name: 'buildNumber', value: String(props.buildNumber) },
    { name: 'environment', value: props.environment },
    { name: 'commitSha', value: props.commitSha.slice(0, 7) },
  ];
  return (
    <div class="mt-3">
      <p class="mb-1.5 text-xs font-medium text-neutral-500">Injected by the release</p>
      <ul class="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
        <For each={vars()}>
          {(v) => (
            <li class="rounded-lg border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 dark:border-neutral-800 dark:bg-neutral-900/40">
              <p class="truncate text-[11px] text-neutral-500">{v.name}</p>
              <p class="truncate font-mono text-xs" title={v.value}>
                {v.value}
              </p>
            </li>
          )}
        </For>
      </ul>
    </div>
  );
};

const workflowProblem = (meta: WorkflowFileMeta): string | null => {
  if (meta.error === 'parse_error') return 'YAML could not be parsed';
  if (!meta.dispatchable) return 'no workflow_dispatch trigger';
  return null;
};

/**
 * Draft step ② — pick workflow files to dispatch at the pinned SHA. Only the
 * adapter's release workflows are selectable (UCH-256), shown by mapped title;
 * unsupported or invalid files (no workflow_dispatch / parse error) are hidden
 * behind a "Show all" toggle and unselectable. A fresh draft pre-fills from
 * the channel defaults (or the adapter templates present at the ref).
 * Override inputs are hidden behind "Customize inputs".
 */
export const WorkflowsSection = (props: {
  productId: string;
  environment: ReleaseEnvironment;
  version: string;
  buildNumber: number;
  commitSha: string;
  selected: string[];
  overrides: WorkflowInputOverrides;
  /** Non-empty = auto Publish to testers on success (UCH-513). */
  autoDistributeGroups: string[];
  canEdit: boolean;
  saving: boolean;
  onSave: (
    selectedWorkflows: string[],
    overrides: WorkflowInputOverrides,
    autoDistributeGroups: string[],
  ) => void;
}) => {
  // includeDisabled: a disabled product's draft must still resolve its adapter,
  // or seeding never runs and the editor is permanently disabled (UCH-294).
  const products = useReleaseProducts(true);
  const workflows = useGitWorkflows(() => ({ productId: props.productId, ref: props.commitSha }));

  const [selected, setSelected] = createSignal<string[]>([]);
  const [inputs, setInputs] = createSignal<WorkflowInputOverrides>({});
  const [customizing, setCustomizing] = createSignal<Set<string>>(new Set());
  const [seeded, setSeeded] = createSignal(false);
  const [showAll, setShowAll] = createSignal(false);
  const [autoEnabled, setAutoEnabled] = createSignal(false);
  const [autoGroups, setAutoGroups] = createSignal<string[]>([]);

  const product = () => (products.data?.data ?? []).find((p) => p.id === props.productId);
  const channel = () => product()?.channels.find((c) => c.environment === props.environment);
  const metas = () => workflows.data?.data ?? [];

  // Unselectable files (not an adapter release workflow / no workflow_dispatch /
  // parse error) are hidden behind "Show all".
  const rowProblem = (meta: WorkflowFileMeta): string | null => {
    const adapter = product()?.adapter;
    if (adapter && !adapterWorkflowFor(adapter, meta.file))
      return `not supported by the ${RELEASE_ADAPTER_LABELS[adapter]} adapter`;
    return workflowProblem(meta);
  };
  const selectable = createMemo(() => metas().filter((m) => !rowProblem(m)));
  const hidden = createMemo(() => metas().filter((m) => rowProblem(m) !== null));
  const validFiles = createMemo(() => new Set(selectable().map((m) => m.file)));

  // Seed the editor once workflow metadata AND the product (adapter) are
  // available — validity depends on the adapter. If the draft already has a
  // selection, restore it; otherwise pre-fill from channel defaults ∩ valid,
  // falling back to every valid adapter workflow at the ref.
  createEffect(() => {
    if (seeded() || metas().length === 0 || !product()) return;
    const valid = validFiles();
    let initial = props.selected.filter((f) => valid.has(f));
    if (props.selected.length === 0) {
      const fromDefaults = (channel()?.defaultWorkflows ?? []).filter((f) => valid.has(f));
      initial = fromDefaults.length > 0 ? fromDefaults : [...valid];
    }
    setSelected(initial);
    // Open every selected workflow's inputs panel up front — no per-card click
    // needed right after the ref is pinned.
    setCustomizing(new Set(initial));
    setInputs(props.overrides);
    setAutoEnabled(props.autoDistributeGroups.length > 0);
    setAutoGroups(props.autoDistributeGroups);
    setSeeded(true);
  });

  // UCH-513: auto Publish to testers — only meaningful when the mobile adhoc
  // workflow is in the selection; deselecting it saves groups as [].
  const adhocSelected = createMemo(
    () =>
      product()?.adapter === 'mobile_app' &&
      selected().some((f) => workflowStem(f) === 'release-mobile-adhoc'),
  );
  const autoGroupsToSave = () => (adhocSelected() && autoEnabled() ? autoGroups() : []);
  const toggleAuto = (checked: boolean) => {
    setAutoEnabled(checked);
    // First enable with nothing picked → prefill from the channel defaults.
    if (checked && autoGroups().length === 0) setAutoGroups(channel()?.fadTesterGroups ?? []);
  };

  const missingTemplates = createMemo(() => {
    const adapter = product()?.adapter;
    if (!adapter || metas().length === 0) return [];
    const present = new Set(metas().map((m) => workflowStem(m.file)));
    return ADAPTER_WORKFLOW_TEMPLATES[adapter].filter((t) => !present.has(t.stem));
  });

  const toggle = (file: string, checked: boolean) =>
    setSelected(checked ? [...selected(), file] : selected().filter((f) => f !== file));

  const setInput = (file: string, name: string, value: string) => {
    const forFile = { ...(inputs()[file] ?? {}) };
    if (value) forFile[name] = value;
    else delete forFile[name];
    const next = { ...inputs(), [file]: forFile };
    if (Object.keys(forFile).length === 0) delete next[file];
    setInputs(next);
  };

  const toggleCustomize = (file: string) => {
    const next = new Set(customizing());
    next.has(file) ? next.delete(file) : next.add(file);
    setCustomizing(next);
  };

  // Only keep overrides for still-selected files.
  const cleanOverrides = (): WorkflowInputOverrides => {
    const result: WorkflowInputOverrides = {};
    for (const file of selected()) {
      const forFile = inputs()[file];
      if (forFile && Object.keys(forFile).length > 0) result[file] = forFile;
    }
    return result;
  };

  const workflowRow = (meta: GitWorkflowRow) => {
    const problem = () => rowProblem(meta);
    const template = () => {
      const adapter = product()?.adapter;
      return adapter ? adapterWorkflowFor(adapter, meta.file) : null;
    };
    const overridable = () => meta.inputs.filter((i) => !SYSTEM_INPUT_NAMES.has(i.name));
    const isSelected = () => selected().includes(meta.file);
    const clickable = () => props.canEdit && problem() === null;
    const customizeOpen = () => isSelected() && customizing().has(meta.file);
    return (
      <div
        class={cn(
          'rounded-lg border bg-surface p-2.5 transition-colors dark:bg-surface-dark',
          isSelected()
            ? 'border-brand-400 bg-brand-50/50 dark:border-brand-700 dark:bg-brand-900/20'
            : 'border-neutral-200 dark:border-neutral-800',
          problem() !== null && 'opacity-60',
          clickable() && 'cursor-pointer',
        )}
        onClick={(e) => {
          // Ignore clicks on interactive elements (checkbox label, buttons, inputs…)
          // so card-level toggling doesn't double-fire.
          if (!clickable()) return;
          if (e.target.closest('a,button,input,label,select,textarea')) return;
          toggle(meta.file, !isSelected());
        }}
      >
        <div class="flex min-h-8 items-center gap-2">
          <Checkbox
            checked={isSelected()}
            onChange={(checked) => toggle(meta.file, checked)}
            disabled={!props.canEdit || problem() !== null}
            label={
              <Show when={template()} fallback={<code class="text-sm">{meta.file}</code>}>
                {(t) => (
                  <span class="inline-flex items-baseline gap-2">
                    <span class="text-sm">{t().title}</span>
                    <code class="text-xs text-neutral-500">{meta.file}</code>
                  </span>
                )}
              </Show>
            }
          />
          <Show when={problem()}>
            <span class="text-xs text-red-500">— {problem()}</span>
          </Show>
          <Show when={!problem() && !meta.registered}>
            <span class="text-xs text-amber-600 dark:text-amber-400">
              — not on the default branch yet; GitHub can't dispatch it until the file lands there
              once
            </span>
          </Show>
          <Show when={overridable().length > 0}>
            <Button
              size="sm"
              variant="ghost"
              inert={!isSelected()}
              class={cn(
                'ml-auto transition-opacity duration-200 motion-reduce:transition-none',
                !isSelected() && 'opacity-0',
              )}
              onClick={() => toggleCustomize(meta.file)}
            >
              {customizing().has(meta.file) ? 'Hide inputs' : 'Customize inputs'}
            </Button>
          </Show>
        </div>
        <Show when={overridable().length > 0}>
          {/* Slide open/closed via the CSS grid-rows trick (same as "Show all" below). */}
          <div
            inert={!customizeOpen()}
            onClick={(e) => e.stopPropagation()}
            class={cn(
              'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
              customizeOpen() ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
            )}
          >
            <div class="overflow-hidden">
              <div class="ml-6 flex flex-col gap-1.5 pt-1.5">
                <For each={overridable()}>
                  {(input) => (
                    <div class="grid grid-cols-[8rem_minmax(0,1fr)] items-center gap-x-2 gap-y-0.5 text-xs">
                      <span class="font-mono text-neutral-500">
                        {input.name}
                        {input.required ? ' *' : ''}
                      </span>
                      <Show
                        when={input.options.length > 0}
                        fallback={
                          <Show
                            when={input.type === 'boolean'}
                            fallback={
                              <input
                                aria-label={input.name}
                                value={inputs()[meta.file]?.[input.name] ?? ''}
                                onInput={(e) =>
                                  setInput(meta.file, input.name, e.currentTarget.value)
                                }
                                disabled={!props.canEdit}
                                placeholder={input.default || 'value'}
                                class="h-10 w-full rounded-lg border border-neutral-300 bg-white px-3 font-mono text-sm outline-none focus:border-brand-500 dark:border-neutral-700 dark:bg-neutral-900"
                              />
                            }
                          >
                            <Checkbox
                              aria-label={input.name}
                              checked={
                                (inputs()[meta.file]?.[input.name] ?? input.default) === 'true'
                              }
                              onChange={(v) => {
                                // Same semantics as the Select: matching the
                                // default clears the override instead of saving it.
                                const next = v ? 'true' : 'false';
                                const dflt = input.default === 'true' ? 'true' : 'false';
                                setInput(meta.file, input.name, next === dflt ? '' : next);
                              }}
                              disabled={!props.canEdit}
                            />
                          </Show>
                        }
                      >
                        <Select
                          options={[
                            {
                              value: '',
                              label: input.default ? `Default (${input.default})` : 'Default',
                            },
                            ...input.options.map((o) => ({ value: o, label: o })),
                          ]}
                          value={inputs()[meta.file]?.[input.name] ?? ''}
                          onChange={(v) => setInput(meta.file, input.name, v)}
                          disabled={!props.canEdit}
                        />
                      </Show>
                      <Show when={input.description}>
                        <p class="col-start-2 text-xs text-neutral-500">{input.description}</p>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
            </div>
          </div>
        </Show>
      </div>
    );
  };

  return (
    <Show
      when={props.commitSha}
      fallback={<p class="text-sm text-neutral-500">Pin a ref first (step 1).</p>}
    >
      <Show
        when={metas().length > 0}
        fallback={
          <p class="text-sm text-neutral-500">
            {workflows.isPending ? 'Loading workflows…' : 'No workflow files found at this ref.'}
          </p>
        }
      >
        <Show when={missingTemplates().length > 0}>
          <p class="mb-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            Adapter release workflow not found at this ref:{' '}
            {missingTemplates()
              .map((t) => t.title)
              .join(', ')}
            .
          </p>
        </Show>

        <div class="flex flex-col gap-2">
          <For each={selectable()}>{workflowRow}</For>
        </div>

        <Show when={hidden().length > 0}>
          <button
            type="button"
            class="mt-2 flex items-center gap-1 text-xs text-neutral-500 hover:underline"
            aria-expanded={showAll()}
            onClick={() => setShowAll(!showAll())}
          >
            <Icon
              name="angle-small-down"
              class={cn(
                'shrink-0 transition-transform duration-200 motion-reduce:transition-none',
                !showAll() && '-rotate-90',
              )}
            />
            {showAll()
              ? 'Hide unsupported workflows'
              : `Show all workflows (${hidden().length} hidden)`}
          </button>
          {/* Expand/collapse via the CSS grid-rows trick so height animates without JS measuring. */}
          <div
            inert={!showAll()}
            class={cn(
              'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
              showAll() ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
            )}
          >
            <div class="overflow-hidden">
              <div class="flex flex-col gap-2 pt-2">
                <For each={hidden()}>{workflowRow}</For>
              </div>
            </div>
          </div>
        </Show>

        <InjectedVars
          version={props.version}
          buildNumber={props.buildNumber}
          environment={props.environment}
          commitSha={props.commitSha}
        />

        {/* Auto publish to testers (UCH-513) — revealed with the grid-rows trick. */}
        <div
          inert={!adhocSelected()}
          class={cn(
            'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
            adhocSelected() ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
          )}
        >
          <div class="overflow-hidden">
            <div class="mt-3 flex flex-col gap-2 rounded-lg border border-neutral-200 p-2.5 dark:border-neutral-800">
              <Checkbox
                checked={autoEnabled()}
                onChange={toggleAuto}
                disabled={!props.canEdit}
                label="Auto publish to testers when this release succeeds"
              />
              <Show when={autoEnabled()}>
                <div class="ml-6">
                  <FadGroupPicker
                    productId={props.productId}
                    environment={props.environment}
                    value={autoGroups()}
                    onChange={setAutoGroups}
                    disabled={!props.canEdit}
                    label="Tester groups"
                  />
                  <Show when={autoGroups().length === 0}>
                    <p class="mt-1 text-xs text-red-600">Pick at least one tester group</p>
                  </Show>
                </div>
              </Show>
            </div>
          </div>
        </div>

        <Show when={props.canEdit}>
          <div class="mt-3 flex justify-end">
            <Button
              onClick={() => props.onSave(selected(), cleanOverrides(), autoGroupsToSave())}
              loading={props.saving}
              disabled={
                selected().length === 0 ||
                (adhocSelected() && autoEnabled() && autoGroups().length === 0)
              }
            >
              Save workflows
            </Button>
          </div>
        </Show>
      </Show>
    </Show>
  );
};
